import Link from '@tiptap/extension-link';
import type { Editor } from '@tiptap/react';
import { useState, type ReactNode } from 'react';

import { CustomBraidTextField } from 'src/app/shared/components/CustomBraidTextField';
import { RichTextEditorProvider } from 'src/app/shared/components/RichTextEditor';

import { TextEditorContainer } from '../TextEditorContainer';

import { MenuBar } from './components/MenuBar';

import { textEditorStyle } from './index.css';

interface Props {
  id?: string;
  characterLimit?: number;
  helpText?: ReactNode;
  message?: string | null;
  onBlur?: () => void;
  onChange?: (data: any) => void;
  onFocus?: () => void;
  value?: string;
  valid?: boolean;
  enableHyperlinks?: boolean;
}

export const RichTextEditorTipTap = ({
  characterLimit,
  helpText,
  id,
  message,
  valid,
  onBlur,
  onChange,
  onFocus,
  value,
  enableHyperlinks,
}: Props) => {
  const [contentHtml, setContentHtml] = useState<string>(value ?? '');
  const [isFocused, setIsFocused] = useState(false);

  const containerProps = {
    characterLimit,
    helpText,
    id,
    message,
    sanitize: false,
    valid,
    value: contentHtml,
  };

  const onUpdate = ({ editor }: { editor: Editor }) => {
    setContentHtml(editor.getHTML());
    if (onChange) {
      onChange(editor.getText().length !== 0 ? editor.getHTML() : '');
    }
  };

  const onFocusProp = () => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  const onBlurProp = () => {
    setIsFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <TextEditorContainer {...containerProps}>
      <CustomBraidTextField isFocused={isFocused} isInvalid={!valid}>
        <RichTextEditorProvider
          onBlur={onBlurProp}
          extensions={
            enableHyperlinks
              ? [
                  Link.configure({
                    defaultProtocol: 'https',
                    openOnClick: false,
                  }),
                ]
              : undefined
          }
          onUpdate={onUpdate}
          onFocus={onFocusProp}
          initialContent={contentHtml}
          editorClass={textEditorStyle}
          slotBefore={<MenuBar enableHyperlinks={enableHyperlinks} />}
        />
      </CustomBraidTextField>
    </TextEditorContainer>
  );
};
